/**
 * Tour mode — the single switch that tells the application a Help & Tour is
 * driving the screen, so data comes from the sample layer instead of the API.
 *
 * This is a plain module-level flag rather than React state on purpose.
 * `startTour` navigates to the flow's first route in the same tick it flips the
 * flag, and the page mounted by that navigation fires its data fetch before any
 * context value could propagate. The axios layer therefore has to be able to
 * ask "are we in a tour?" synchronously, from outside React.
 *
 * It is owned by {@link TourProvider}: `startTour` enables it, and every path
 * that ends a run (`stopTour`, the popover's close/finish, resetTourState)
 * disables it. Nothing else should write to it.
 */

let tourModeActive = false;
let activeFlowId: string | null = null;

type TourModeListener = (active: boolean) => void;
const listeners = new Set<TourModeListener>();

/**
 * True while a Help & Tour is running. Read by the api-client interceptor and
 * by the handful of components that swap a live handler for a demo one.
 */
export const isTourMode = (): boolean => tourModeActive;

/** Selectors the step on screen points at; see {@link setTourStepTargets}. */
let stepTargets: string[] = [];

const isStepTarget = (control: Element): boolean =>
  stepTargets.some(selector => {
    try {
      return control.matches(selector);
    } catch {
      return false;
    }
  });

/**
 * True when a tour is running and this event came from the user rather than
 * from the tour itself, on a control the current step does not point at.
 * Views a tour steers (the Administration Hub's cards, module tabs and
 * Maker / Checker switch) ignore such events, so the screen cannot drift from
 * what the popover describes. A click the step asks for (its own element, its
 * `advanceClick`, its click `targetSelector` or its `allowClick`) goes through,
 * as do the tour's own programmatic clicks, which are untrusted.
 */
export const isBlockedByTour = (event?: {
  nativeEvent?: Event;
  isTrusted?: boolean;
  currentTarget?: EventTarget | null;
}): boolean => {
  if (!tourModeActive) return false;
  if (!(event?.nativeEvent?.isTrusted ?? event?.isTrusted ?? false)) {
    return false;
  }
  const control = event?.currentTarget;
  return !(control instanceof Element && isStepTarget(control));
};

/**
 * Name the controls the step on screen points at, so the user's own click on
 * one of them is not swallowed by {@link isBlockedByTour}. Called by the tour
 * provider each time a step is highlighted, and cleared between pages.
 */
export const setTourStepTargets = (selectors: (string | undefined)[]) => {
  stepTargets = selectors.filter((s): s is string => !!s);
};

/** The flow currently running, for sample data that varies per tour. */
export const getTourFlowId = (): string | null => activeFlowId;

/**
 * Enable/disable tour mode. Called only by the tour provider.
 * Enabling resets the demo state so every run starts from the same place.
 */
export const setTourMode = (active: boolean, flowId: string | null = null) => {
  if (tourModeActive === active && activeFlowId === flowId) return;
  tourModeActive = active;
  activeFlowId = active ? flowId : null;
  stepTargets = [];
  listeners.forEach(listener => {
    try {
      listener(active);
    } catch {
      /* a bad listener must not break the tour */
    }
  });
};

/** Subscribe to tour-mode changes. Returns an unsubscribe function. */
export const onTourModeChange = (listener: TourModeListener): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
